import { useState } from "react";
import api from "../lib/api";
import { Card, Badge, useFetch } from "./Shared";
import { toast } from "sonner";
import { Sparkle, Plus, CheckCircle, Trash, FloppyDisk, ChatText, Lightbulb, Flag, CalendarBlank, XCircle, UsersThree } from "@phosphor-icons/react";

const LEVELS = [
  { key: "DECLARED", label: "Declarado", tone: "neutral", hint: "Lo que la persona dice" },
  { key: "REALIZED", label: "Realizado", tone: "warning", hint: "Acción concreta declarada" },
  { key: "VERIFIED", label: "Verificado", tone: "forest", hint: "Confirmado por App HM / humano" },
];
const CATEGORIES = ["participation", "action", "referral", "idea", "experience", "other"];
const CAT_LABEL = {
  participation: "participación", action: "acción", referral: "referido",
  idea: "idea", experience: "experiencia", other: "otro",
};
const RESPONSIBLES = ["ASPIRANT", "HM", "SYSTEM", "OTHER"];
const RESP_LABEL = { ASPIRANT: "Aspirante", HM: "HM", SYSTEM: "Sistema", OTHER: "Otro" };
const ACTION_TYPES = ["session", "app_download", "referral", "review", "activity", "other"];
const ACTION_TYPE_LABEL = {
  session: "sesión", app_download: "descargar app", referral: "referido",
  review: "revisión", activity: "actividad", other: "otro",
};

export default function AspirantDossierCard({ userId }) {
  const { data, loading, reload } = useFetch(
    () => api.get(`/aspirants/${userId}/dossier`).then((r) => r.data), [userId]
  );
  const [extracting, setExtracting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newEv, setNewEv] = useState({ evidence_level: "DECLARED", category: "action", description: "" });
  const [fields, setFields] = useState(null);
  const [savingFields, setSavingFields] = useState(false);
  const [note, setNote] = useState("");
  const [newNa, setNewNa] = useState({ action_type: "session", description: "", responsible_party: "ASPIRANT", due_at: "", due_text: "" });
  const [addingNa, setAddingNa] = useState(false);
  const [ivSchedule, setIvSchedule] = useState("");
  const [ivReason, setIvReason] = useState("");
  const [ivNotYetAction, setIvNotYetAction] = useState("");
  const [ivBusy, setIvBusy] = useState(false);
  const [fmRef, setFmRef] = useState("");
  const [fmBusy, setFmBusy] = useState(false);
  const [newRef, setNewRef] = useState({ referred_name: "", referred_phone: "" });
  const [refBusy, setRefBusy] = useState(false);

  const d = data?.dossier;
  const evByLevel = data?.evidence_by_level || {};
  const counts = data?.counts || {};
  const nextActions = data?.next_actions || [];
  const referrals = data?.referrals || [];
  const refCounts = data?.referral_counts || { declared: 0, verified: 0 };

  // sync editable fields once loaded
  if (d && fields === null) {
    setFields({
      participation: d.participation || "",
      hm_experience: d.hm_experience || "",
      summary: d.summary || "",
    });
  }

  const extract = async () => {
    setExtracting(true);
    try {
      const { data: res } = await api.post(`/aspirants/${userId}/extract-evidence`, {});
      const na = res.next_actions_created ? `, ${res.next_actions_created} próxima(s) acción(es)` : "";
      toast.success(`IA extrajo ${res.created} evidencia(s)${na}${res.dossier_updated ? " y actualizó el perfil" : ""}`);
      setFields(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al extraer con IA");
    } finally { setExtracting(false); }
  };

  const addEvidence = async () => {
    if (!newEv.description.trim()) { toast.error("Escribe una descripción"); return; }
    setAdding(true);
    try {
      await api.post(`/aspirants/${userId}/evidence`, newEv);
      toast.success("Evidencia agregada");
      setNewEv({ evidence_level: "DECLARED", category: "action", description: "" });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setAdding(false); }
  };

  const changeLevel = async (ev, level) => {
    try {
      await api.patch(`/aspirants/${userId}/evidence/${ev.id}`, { evidence_level: level });
      toast.success(level === "VERIFIED" ? "Marcada como Verificada" : `Movida a ${level}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const delEvidence = async (ev) => {
    if (!window.confirm("¿Eliminar esta evidencia?")) return;
    try {
      await api.delete(`/aspirants/${userId}/evidence/${ev.id}`);
      toast.success("Eliminada");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const saveFields = async () => {
    setSavingFields(true);
    try {
      await api.patch(`/aspirants/${userId}/dossier`, fields);
      toast.success("Perfil del expediente guardado");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSavingFields(false); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/aspirants/${userId}/notes`, { text: note.trim() });
      setNote("");
      toast.success("Nota agregada");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const addNextAction = async () => {
    if (!newNa.description.trim()) { toast.error("Describe el compromiso"); return; }
    setAddingNa(true);
    try {
      const payload = { action_type: newNa.action_type, description: newNa.description, responsible_party: newNa.responsible_party,
        due_at: newNa.due_at ? new Date(newNa.due_at).toISOString() : null,
        due_text: newNa.due_text?.trim() || null };
      await api.post(`/aspirants/${userId}/next-actions`, payload);
      toast.success("Próxima acción registrada");
      setNewNa({ action_type: "session", description: "", responsible_party: "ASPIRANT", due_at: "", due_text: "" });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setAddingNa(false); }
  };

  const setNaStatus = async (na, status) => {
    try {
      await api.patch(`/aspirants/${userId}/next-actions/${na.id}`, { status });
      toast.success(status === "COMPLETED" ? "Marcada como cumplida" : "Cancelada");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const delNextAction = async (na) => {
    if (!window.confirm("¿Eliminar esta próxima acción?")) return;
    try {
      await api.delete(`/aspirants/${userId}/next-actions/${na.id}`);
      toast.success("Eliminada");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const scheduleInterview = async () => {
    if (!ivSchedule) { toast.error("Elige fecha y hora"); return; }
    setIvBusy(true);
    try {
      await api.post(`/aspirants/${userId}/interview/schedule`, { scheduled_at: new Date(ivSchedule).toISOString() });
      toast.success("Entrevista agendada");
      setIvSchedule("");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setIvBusy(false); }
  };

  const submitResult = async (result) => {
    if (result === "NOT_YET" && !ivNotYetAction.trim()) {
      toast.error("Para 'Todavía no' indica la acción específica"); return;
    }
    if (!window.confirm(`Registrar resultado: ${result}. Esta es una decisión humana. ¿Continuar?`)) return;
    setIvBusy(true);
    try {
      await api.post(`/aspirants/${userId}/interview/result`, {
        result,
        reason: ivReason.trim() || null,
        next_action_description: result === "NOT_YET" ? ivNotYetAction.trim() : null,
      });
      toast.success(`Resultado registrado: ${result}`);
      setIvReason(""); setIvNotYetAction("");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setIvBusy(false); }
  };

  const setFormalization = async (status) => {
    if (!window.confirm(`Cambiar formalización a ${status}. ¿Continuar?`)) return;
    setFmBusy(true);
    try {
      await api.post(`/aspirants/${userId}/formalization`, {
        status,
        contract_reference: fmRef.trim() || null,
      });
      toast.success(`Formalización: ${status}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setFmBusy(false); }
  };

  const addReferral = async () => {
    if (!newRef.referred_name.trim()) { toast.error("Nombre del referido requerido"); return; }
    setRefBusy(true);
    try {
      await api.post(`/aspirants/${userId}/referrals`, {
        referred_name: newRef.referred_name.trim(),
        referred_phone: newRef.referred_phone.trim() || null,
      });
      toast.success("Referido registrado (Declarado)");
      setNewRef({ referred_name: "", referred_phone: "" });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setRefBusy(false); }
  };

  const verifyReferral = async (r) => {
    const ref = window.prompt("Referencia de registro en App HM (opcional):", "");
    if (ref === null) return;
    try {
      await api.patch(`/aspirants/${userId}/referrals/${r.id}/verify`, { app_registration_ref: ref || null });
      toast.success("Referido verificado (registrado en App HM)");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const delReferral = async (r) => {
    if (!window.confirm("¿Eliminar este referido?")) return;
    try {
      await api.delete(`/aspirants/${userId}/referrals/${r.id}`);
      toast.success("Eliminado");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  if (loading) return <Card className="p-5" testid="aspirant-dossier-card"><div className="text-sm text-carbon-500">Cargando expediente…</div></Card>;

  return (
    <Card className="p-5" testid="aspirant-dossier-card">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <Sparkle size={16} className="text-terracotta" />
        <div className="section-label">Expediente del Aspirante</div>
        <Badge tone="terracotta">Journey V2</Badge>
        <button onClick={extract} disabled={extracting} data-testid="extract-evidence-btn"
          className="ml-auto bg-terracotta text-white text-xs px-3 py-1.5 rounded flex items-center gap-1.5 hover:bg-terracotta-hover disabled:opacity-60">
          <Sparkle size={13} /> {extracting ? "Extrayendo…" : "Extraer evidencia con IA"}
        </button>
      </div>
      <p className="text-xs text-carbon-500 mb-4">
        Organiza la evidencia en tres niveles. La decisión de Embajador es <strong>humana</strong>; esto solo estructura la información.
      </p>

      {/* Contadores por nivel */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {LEVELS.map((lv) => (
          <div key={lv.key} className="bg-page rounded p-2 border border-carbon-200" data-testid={`evidence-count-${lv.key.toLowerCase()}`}>
            <div className="flex items-center gap-1.5">
              <Badge tone={lv.tone}>{lv.label}</Badge>
              <span className="font-heading text-xl ml-auto">{counts[lv.key] || 0}</span>
            </div>
            <div className="text-[10px] text-carbon-500 mt-1">{lv.hint}</div>
          </div>
        ))}
      </div>

      {/* Próxima acción · compromisos {qué, responsable, cuándo} */}
      <div className="border border-terracotta/30 rounded p-3 mb-5 bg-terracotta/5">
        <div className="text-[11px] uppercase tracking-wide text-carbon-600 mb-2 flex items-center gap-1">
          <Flag size={13} className="text-terracotta" /> Próxima acción
          {data?.open_next_actions > 0 && <Badge tone="terracotta">{data.open_next_actions} pendiente(s)</Badge>}
        </div>
        <div className="space-y-1.5 mb-3">
          {nextActions.map((na) => {
            const done = na.status === "COMPLETED";
            const cancelled = na.status === "CANCELLED";
            return (
              <div key={na.id} className={`flex items-start gap-2 border border-carbon-200 rounded p-2 bg-surface ${done || cancelled ? "opacity-60" : ""}`} data-testid={`next-action-${na.id}`}>
                <Badge tone="neutral">{ACTION_TYPE_LABEL[na.action_type] || na.action_type}</Badge>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm text-carbon-900 ${done ? "line-through" : ""}`}>{na.description}</div>
                  <div className="text-[10px] text-carbon-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span>responsable: <strong>{RESP_LABEL[na.responsible_party] || na.responsible_party}</strong></span>
                    {na.due_at && <span className="flex items-center gap-0.5"><CalendarBlank size={11} /> {new Date(na.due_at).toLocaleString()}</span>}
                    {!na.due_at && <span className="italic">sin fecha</span>}
                    <Badge tone={done ? "forest" : cancelled ? "neutral" : "warning"}>{na.status}</Badge>
                    {na.source === "conversation" && <Badge>IA</Badge>}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {na.status === "PENDING" && (
                    <>
                      <button onClick={() => setNaStatus(na, "COMPLETED")} title="Marcar cumplida" data-testid={`complete-next-action-${na.id}`}
                        className="p-1 text-forest hover:bg-forest/10 rounded"><CheckCircle size={16} /></button>
                      <button onClick={() => setNaStatus(na, "CANCELLED")} title="Cancelar" data-testid={`cancel-next-action-${na.id}`}
                        className="p-1 text-carbon-500 hover:bg-carbon-100 rounded"><XCircle size={15} /></button>
                    </>
                  )}
                  <button onClick={() => delNextAction(na)} title="Eliminar" data-testid={`delete-next-action-${na.id}`}
                    className="p-1 text-signal hover:bg-signal/10 rounded"><Trash size={14} /></button>
                </div>
              </div>
            );
          })}
          {nextActions.length === 0 && <div className="text-xs text-carbon-400">Sin próximas acciones. El seguimiento contextual solo ocurre cuando hay una razón concreta.</div>}
        </div>
        <div className="flex flex-col md:flex-row gap-2">
          <select value={newNa.action_type} onChange={(e) => setNewNa({ ...newNa, action_type: e.target.value })}
            data-testid="new-na-type" className="text-sm bg-surface border border-carbon-200 rounded px-2 py-1.5">
            {ACTION_TYPES.map((t) => <option key={t} value={t}>{ACTION_TYPE_LABEL[t]}</option>)}
          </select>
          <input value={newNa.description} onChange={(e) => setNewNa({ ...newNa, description: e.target.value })}
            placeholder="Compromiso concreto…" data-testid="new-na-description"
            className="flex-1 text-sm bg-surface border border-carbon-200 rounded px-2 py-1.5" />
          <select value={newNa.responsible_party} onChange={(e) => setNewNa({ ...newNa, responsible_party: e.target.value })}
            data-testid="new-na-responsible" className="text-sm bg-surface border border-carbon-200 rounded px-2 py-1.5">
            {RESPONSIBLES.map((r) => <option key={r} value={r}>{RESP_LABEL[r]}</option>)}
          </select>
          <input type="datetime-local" value={newNa.due_at} onChange={(e) => setNewNa({ ...newNa, due_at: e.target.value })}
            data-testid="new-na-due" className="text-sm bg-surface border border-carbon-200 rounded px-2 py-1.5" />
          <input value={newNa.due_text} onChange={(e) => setNewNa({ ...newNa, due_text: e.target.value })}
            placeholder='o en texto: "el lunes", "en 2 días"' data-testid="new-na-due-text"
            className="text-sm bg-surface border border-carbon-200 rounded px-2 py-1.5" />
          <button onClick={addNextAction} disabled={addingNa} data-testid="add-next-action-btn"
            className="bg-terracotta text-white text-sm px-3 py-1.5 rounded hover:bg-terracotta-hover disabled:opacity-60">
            {addingNa ? "…" : "Registrar"}
          </button>
        </div>
      </div>

      {/* Evidencia por nivel */}
      <div className="space-y-3 mb-5">
        {LEVELS.map((lv) => (
          <div key={lv.key}>
            <div className="text-[11px] uppercase tracking-wide text-carbon-500 mb-1">{lv.label}</div>
            <div className="space-y-1.5">
              {(evByLevel[lv.key] || []).map((ev) => (
                <div key={ev.id} className="flex items-start gap-2 border border-carbon-200 rounded p-2 bg-surface" data-testid={`evidence-item-${ev.id}`}>
                  <Badge>{CAT_LABEL[ev.category] || ev.category}</Badge>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-carbon-900">{ev.description}</div>
                    <div className="text-[10px] text-carbon-500 mt-0.5">
                      fuente: {ev.source}{ev.verified_by ? ` · verificó: ${ev.verified_by}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {lv.key !== "VERIFIED" && (
                      <button onClick={() => changeLevel(ev, "VERIFIED")} title="Marcar como Verificado (fuente confiable)"
                        data-testid={`verify-evidence-${ev.id}`}
                        className="p-1 text-forest hover:bg-forest/10 rounded">
                        <CheckCircle size={16} />
                      </button>
                    )}
                    <button onClick={() => delEvidence(ev)} title="Eliminar" data-testid={`delete-evidence-${ev.id}`}
                      className="p-1 text-signal hover:bg-signal/10 rounded">
                      <Trash size={14} />
                    </button>
                  </div>
                </div>
              ))}
              {(evByLevel[lv.key] || []).length === 0 && (
                <div className="text-xs text-carbon-400">Sin evidencia {lv.label.toLowerCase()}.</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Agregar evidencia manual */}
      <div className="border border-carbon-200 rounded p-3 mb-5 bg-page">
        <div className="text-[11px] uppercase tracking-wide text-carbon-500 mb-2 flex items-center gap-1"><Plus size={12} /> Agregar evidencia manual</div>
        <div className="flex flex-col md:flex-row gap-2">
          <select value={newEv.evidence_level} onChange={(e) => setNewEv({ ...newEv, evidence_level: e.target.value })}
            data-testid="new-evidence-level" className="text-sm bg-surface border border-carbon-200 rounded px-2 py-1.5">
            {LEVELS.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
          </select>
          <select value={newEv.category} onChange={(e) => setNewEv({ ...newEv, category: e.target.value })}
            data-testid="new-evidence-category" className="text-sm bg-surface border border-carbon-200 rounded px-2 py-1.5">
            {CATEGORIES.map((c) => <option key={c} value={c}>{CAT_LABEL[c]}</option>)}
          </select>
          <input value={newEv.description} onChange={(e) => setNewEv({ ...newEv, description: e.target.value })}
            placeholder="Descripción concreta…" data-testid="new-evidence-description"
            className="flex-1 text-sm bg-surface border border-carbon-200 rounded px-2 py-1.5" />
          <button onClick={addEvidence} disabled={adding} data-testid="add-evidence-btn"
            className="bg-forest text-white text-sm px-3 py-1.5 rounded hover:opacity-90 disabled:opacity-60">
            {adding ? "…" : "Agregar"}
          </button>
        </div>
      </div>

      {/* Campos del expediente */}
      {fields && (
        <div className="space-y-3 mb-5">
          <div className="text-[11px] uppercase tracking-wide text-carbon-500">Perfil del expediente</div>
          <TextField label="Participación expresada" value={fields.participation}
            onChange={(v) => setFields({ ...fields, participation: v })} testid="dossier-participation" />
          <TextField label="Experiencia con HM" value={fields.hm_experience}
            onChange={(v) => setFields({ ...fields, hm_experience: v })} testid="dossier-experience" />
          <TextField label="Resumen de trayectoria" value={fields.summary}
            onChange={(v) => setFields({ ...fields, summary: v })} testid="dossier-summary" />
          <button onClick={saveFields} disabled={savingFields} data-testid="save-dossier-btn"
            className="bg-terracotta text-white text-sm px-3 py-1.5 rounded flex items-center gap-1.5 hover:bg-terracotta-hover disabled:opacity-60">
            <FloppyDisk size={14} /> {savingFields ? "Guardando…" : "Guardar perfil"}
          </button>
        </div>
      )}

      {/* Ideas */}
      {(d?.ideas || []).length > 0 && (
        <div className="mb-5">
          <div className="text-[11px] uppercase tracking-wide text-carbon-500 mb-1 flex items-center gap-1"><Lightbulb size={12} /> Ideas aportadas</div>
          <div className="space-y-1">
            {d.ideas.map((it, i) => (
              <div key={i} className="text-sm bg-page border border-carbon-200 rounded p-2">{it}</div>
            ))}
          </div>
        </div>
      )}

      {/* Entrevista + decisión humana */}
      <div className="mb-5 border border-carbon-200 rounded p-3 bg-page">
        <div className="text-[11px] uppercase tracking-wide text-carbon-500 mb-2 flex items-center gap-2">
          Entrevista · decisión humana
          <Badge tone="neutral">{d?.interview?.status || "INTERVIEW_PENDING"}</Badge>
          {d?.interview?.result && (
            <Badge tone={d.interview.result === "APPROVED" ? "forest" : d.interview.result === "NOT_APPROVED" ? "signal" : "warning"}>
              {d.interview.result}
            </Badge>
          )}
        </div>

        {d?.interview?.scheduled_at && (
          <div className="text-xs text-carbon-500 mb-2 flex items-center gap-1">
            <CalendarBlank size={12} /> Agendada: {new Date(d.interview.scheduled_at).toLocaleString()}
          </div>
        )}

        {/* Agendar */}
        <div className="flex flex-col md:flex-row gap-2 mb-3">
          <input type="datetime-local" value={ivSchedule} onChange={(e) => setIvSchedule(e.target.value)}
            data-testid="interview-schedule-input" className="text-sm bg-surface border border-carbon-200 rounded px-2 py-1.5" />
          <button onClick={scheduleInterview} disabled={ivBusy} data-testid="schedule-interview-btn"
            className="bg-carbon-800 text-white text-sm px-3 py-1.5 rounded hover:opacity-90 disabled:opacity-60 flex items-center gap-1.5">
            <CalendarBlank size={14} /> Agendar entrevista
          </button>
        </div>

        {/* Resultado (decisión humana) */}
        <div className="space-y-2">
          <input value={ivReason} onChange={(e) => setIvReason(e.target.value)}
            placeholder="Motivo / notas de la decisión (opcional)" data-testid="interview-reason-input"
            className="w-full text-sm bg-surface border border-carbon-200 rounded px-2 py-1.5" />
          <input value={ivNotYetAction} onChange={(e) => setIvNotYetAction(e.target.value)}
            placeholder="Si es 'Todavía no': acción específica que debe cumplir…" data-testid="interview-notyet-action-input"
            className="w-full text-sm bg-surface border border-carbon-200 rounded px-2 py-1.5" />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => submitResult("APPROVED")} disabled={ivBusy} data-testid="interview-approved-btn"
              className="bg-forest text-white text-sm px-3 py-1.5 rounded hover:opacity-90 disabled:opacity-60 flex items-center gap-1.5">
              <CheckCircle size={14} /> Aprobado
            </button>
            <button onClick={() => submitResult("NOT_YET")} disabled={ivBusy} data-testid="interview-notyet-btn"
              className="bg-terracotta text-white text-sm px-3 py-1.5 rounded hover:bg-terracotta-hover disabled:opacity-60 flex items-center gap-1.5">
              <Flag size={14} /> Todavía no
            </button>
            <button onClick={() => submitResult("NOT_APPROVED")} disabled={ivBusy} data-testid="interview-notapproved-btn"
              className="bg-signal text-white text-sm px-3 py-1.5 rounded hover:opacity-90 disabled:opacity-60 flex items-center gap-1.5">
              <XCircle size={14} /> No aprobado
            </button>
          </div>
          <p className="text-[10px] text-carbon-400">La decisión es humana: la IA nunca aprueba. "Todavía no" exige registrar una acción específica.</p>
        </div>

        {/* Historial */}
        {(d?.interview?.history || []).length > 0 && (
          <div className="mt-3 space-y-1">
            <div className="text-[10px] uppercase tracking-wide text-carbon-400">Historial de decisiones</div>
            {d.interview.history.map((h, i) => (
              <div key={i} className="text-[11px] text-carbon-600 bg-surface border border-carbon-200 rounded px-2 py-1">
                <strong>{h.result}</strong>{h.reason ? ` · ${h.reason}` : ""} · {h.decided_by} · {new Date(h.decided_at).toLocaleString()}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Formalización (§10) · visible tras aprobar la entrevista */}
      {d?.formalization?.status && (
        <div className="mb-5 border border-forest/30 rounded p-3 bg-forest/5" data-testid="formalization-section">
          <div className="text-[11px] uppercase tracking-wide text-carbon-600 mb-2 flex items-center gap-2">
            Formalización
            <Badge tone={d.formalization.status === "AMBASSADOR_ACTIVE" ? "forest" : "neutral"}>{d.formalization.status}</Badge>
          </div>
          <div className="flex items-center gap-1 mb-3 flex-wrap">
            {["CANDIDATE", "CONTRACT_PENDING", "CONTRACT_SIGNED", "AMBASSADOR_ACTIVE"].map((s, i, arr) => {
              const idx = arr.indexOf(d.formalization.status);
              const done = i <= idx;
              return (
                <div key={s} className="flex items-center">
                  <span className={`text-[10px] px-2 py-1 rounded ${done ? "bg-forest text-white" : "bg-carbon-100 text-carbon-500"}`}>
                    {s === "CANDIDATE" ? "Candidato" : s === "CONTRACT_PENDING" ? "Contrato pendiente" : s === "CONTRACT_SIGNED" ? "Contrato firmado" : "Embajador activo"}
                  </span>
                  {i < arr.length - 1 && <span className="mx-0.5 text-carbon-300">›</span>}
                </div>
              );
            })}
          </div>
          {(d.formalization.contract_reference || d.formalization.contract_date || d.formalization.ambassador_activation_date) && (
            <div className="text-[11px] text-carbon-500 mb-2 space-y-0.5">
              {d.formalization.contract_reference && <div>Referencia de contrato: <strong>{d.formalization.contract_reference}</strong></div>}
              {d.formalization.contract_date && <div>Contrato: {new Date(d.formalization.contract_date).toLocaleDateString()}</div>}
              {d.formalization.ambassador_activation_date && <div>Activación embajador: {new Date(d.formalization.ambassador_activation_date).toLocaleDateString()}</div>}
            </div>
          )}
          <input value={fmRef} onChange={(e) => setFmRef(e.target.value)}
            placeholder="Referencia/ID de contrato (opcional, si vive en otra plataforma)"
            data-testid="formalization-ref-input"
            className="w-full text-sm bg-surface border border-carbon-200 rounded px-2 py-1.5 mb-2" />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setFormalization("CONTRACT_PENDING")} disabled={fmBusy} data-testid="fm-contract-pending-btn"
              className="text-sm px-3 py-1.5 rounded border border-carbon-300 hover:bg-carbon-100 disabled:opacity-60">Contrato pendiente</button>
            <button onClick={() => setFormalization("CONTRACT_SIGNED")} disabled={fmBusy} data-testid="fm-contract-signed-btn"
              className="text-sm px-3 py-1.5 rounded border border-carbon-300 hover:bg-carbon-100 disabled:opacity-60">Contrato firmado</button>
            <button onClick={() => setFormalization("AMBASSADOR_ACTIVE")} disabled={fmBusy} data-testid="fm-activate-btn"
              className="bg-forest text-white text-sm px-3 py-1.5 rounded hover:opacity-90 disabled:opacity-60">Activar Embajador</button>
          </div>
        </div>
      )}

      {/* Referidos (§7) · declarado → verificado */}
      <div className="mb-5 border border-carbon-200 rounded p-3 bg-page" data-testid="referrals-section">
        <div className="text-[11px] uppercase tracking-wide text-carbon-500 mb-2 flex items-center gap-2">
          <UsersThree size={13} /> Referidos
          <Badge tone="neutral">{refCounts.declared} declarado(s)</Badge>
          <Badge tone="forest">{refCounts.verified} verificado(s)</Badge>
        </div>
        <div className="space-y-1.5 mb-3">
          {referrals.map((r) => (
            <div key={r.id} className="flex items-start gap-2 border border-carbon-200 rounded p-2 bg-surface" data-testid={`referral-${r.id}`}>
              <Badge tone={r.status === "VERIFIED" ? "forest" : "neutral"}>{r.status === "VERIFIED" ? "Verificado" : "Declarado"}</Badge>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-carbon-900">{r.referred_name}</div>
                <div className="text-[10px] text-carbon-500 mt-0.5">
                  {r.referred_phone ? `tel: ${r.referred_phone}` : "sin teléfono"}
                  {r.app_registration_ref ? ` · App: ${r.app_registration_ref}` : ""}
                  {r.verified_by ? ` · verificó: ${r.verified_by}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {r.status !== "VERIFIED" && (
                  <button onClick={() => verifyReferral(r)} title="Verificar (registrado en App HM)" data-testid={`verify-referral-${r.id}`}
                    className="p-1 text-forest hover:bg-forest/10 rounded"><CheckCircle size={16} /></button>
                )}
                <button onClick={() => delReferral(r)} title="Eliminar" data-testid={`delete-referral-${r.id}`}
                  className="p-1 text-signal hover:bg-signal/10 rounded"><Trash size={14} /></button>
              </div>
            </div>
          ))}
          {referrals.length === 0 && <div className="text-xs text-carbon-400">Sin referidos. Un referido es válido cuando se verifica su registro en la App HM.</div>}
        </div>
        <div className="flex flex-col md:flex-row gap-2">
          <input value={newRef.referred_name} onChange={(e) => setNewRef({ ...newRef, referred_name: e.target.value })}
            placeholder="Nombre del referido" data-testid="new-referral-name"
            className="flex-1 text-sm bg-surface border border-carbon-200 rounded px-2 py-1.5" />
          <input value={newRef.referred_phone} onChange={(e) => setNewRef({ ...newRef, referred_phone: e.target.value })}
            placeholder="Teléfono (opcional)" data-testid="new-referral-phone"
            className="text-sm bg-surface border border-carbon-200 rounded px-2 py-1.5" />
          <button onClick={addReferral} disabled={refBusy} data-testid="add-referral-btn"
            className="bg-carbon-800 text-white text-sm px-3 py-1.5 rounded hover:opacity-90 disabled:opacity-60">
            {refBusy ? "…" : "Agregar"}
          </button>
        </div>
        <p className="text-[10px] text-carbon-400 mt-2">Primera atribución gana: si el teléfono ya está atribuido a otro Aspirante, no se puede duplicar.</p>
      </div>

      {/* Notas HM */}
      <div>
        <div className="text-[11px] uppercase tracking-wide text-carbon-500 mb-1 flex items-center gap-1"><ChatText size={12} /> Notas HM</div>
        <div className="space-y-1.5 mb-2">
          {(d?.notes || []).map((n) => (
            <div key={n.id} className="text-sm bg-page border border-carbon-200 rounded p-2">
              <div>{n.text}</div>
              <div className="text-[10px] text-carbon-500 mt-0.5">{n.author} · {new Date(n.at).toLocaleString()}</div>
            </div>
          ))}
          {(d?.notes || []).length === 0 && <div className="text-xs text-carbon-400">Sin notas.</div>}
        </div>
        <div className="flex gap-2">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Escribe una nota interna…"
            data-testid="dossier-note-input" onKeyDown={(e) => e.key === "Enter" && addNote()}
            className="flex-1 text-sm bg-surface border border-carbon-200 rounded px-2 py-1.5" />
          <button onClick={addNote} data-testid="add-note-btn"
            className="bg-carbon-800 text-white text-sm px-3 py-1.5 rounded hover:opacity-90">Agregar</button>
        </div>
      </div>
    </Card>
  );
}

function TextField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs text-carbon-500">{label}</span>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} data-testid={testid}
        className="w-full text-sm bg-page border border-carbon-200 rounded px-2 py-1.5 mt-1 resize-y" />
    </label>
  );
}
